import { CustomPopulationActionDtoAction } from 'api/generated/schemas/customPopulationActionDtoAction';
import {
  EligibilityCriteriaDtoCriteria,
  EligibilityCriteriaDtoOperand,
} from 'api/generated/schemas';
import type {
  AdjustmentPriorityEntryDto,
  CalculationRuleDto,
  CustomPopulationDraftDto,
  CustomPopulationDto,
  CustomPopulationActionDto,
  CustomPopulationRequest,
  EligibilityCriteriaDto,
  ModeledAdjustmentGridCustomPopulationDto,
  ModeledAdjustmentGridDto,
} from 'api/generated/schemas';

import {
  ELIGIBILITY_CRITERIA_OPTIONS,
  ELIGIBILITY_OPERAND_OPTIONS,
  SPECIAL_AWARD_PRESETS,
} from '../../calculations-step/calculations-step.constants';
import type {
  SpecialAwardAppliesTo,
  SpecialAwardCriteriaBlock,
} from '../../calculations-step/calculations-step.types';
import type { SpecialAwardLogicCategory } from '../../calculations-step/hooks/use-special-award-form';

const POPULATION_NAME_MAX_LENGTH = 150;
const NEW_POPULATION_KEY_PREFIX = 'new-population-';

export const CUSTOM_POPULATION_CRITERIA_OPTIONS = [
  { value: 'STUDENT_DATA', label: 'Student data' },
  { value: 'ELIGIBILITY', label: 'Eligibility' },
] as const;

export interface DraftPopulation {
  id?: number;
  clientKey?: string;
  definition: CustomPopulationRequest;
}

export interface PopulationDraftState {
  populations: DraftPopulation[];
  priorityOrder: AdjustmentPriorityEntryDto[];
}

export type PopulationTab =
  | { key: string; kind: 'regime'; regimeId: number; name: string }
  | {
      key: string;
      kind: 'custom';
      name: string;
      population: DraftPopulation;
      grid: ModeledAdjustmentGridCustomPopulationDto;
    };

export const makeNewPopulationKey = (index: number): string =>
  `${NEW_POPULATION_KEY_PREFIX}${index}`;

export const getRegimeTabKey = (regimeId: number): string =>
  `regime:${regimeId}`;

export const getPopulationTabKey = ({
  id,
  clientKey,
}: Pick<DraftPopulation, 'id' | 'clientKey'>): string =>
  id != null ? `population:${id}` : `draft:${clientKey}`;

export const getPriorityEntryKey = (
  entry: AdjustmentPriorityEntryDto
): string => {
  if (entry.regimeId != null) {
    return getRegimeTabKey(entry.regimeId);
  }

  return getPopulationTabKey({
    id: entry.customPopulationId,
    clientKey: entry.customPopulationKey,
  });
};

export const toPriorityEntry = (
  population: Pick<DraftPopulation, 'id' | 'clientKey'>
): AdjustmentPriorityEntryDto =>
  population.id != null
    ? { customPopulationId: population.id }
    : { customPopulationKey: population.clientKey };

export const toDraftPopulation = (
  dto: CustomPopulationDto
): DraftPopulation => ({
  id: dto.id,
  definition: {
    name: dto.name,
    campusVisit: dto.campusVisit,
    pellGrant: dto.pellGrant,
    fafsaGrant: dto.fafsaGrant,
    customLogicIds: dto.customLogicIds,
    eligibilityCriterias: dto.eligibilityCriterias,
  },
});

export const initPopulationDraft = (
  grid: ModeledAdjustmentGridDto | undefined
): PopulationDraftState => ({
  populations: (grid?.customPopulations ?? [])
    .map((entry) => entry.population)
    .filter((population): population is CustomPopulationDto => !!population)
    .map(toDraftPopulation),
  priorityOrder: grid?.priorityOrder ?? [],
});

export const toPopulationRule = (
  population: DraftPopulation
): CalculationRuleDto => ({
  id: population.id,
  campusVisit: population.definition.campusVisit,
  pellGrant: population.definition.pellGrant,
  fafsaGrant: population.definition.fafsaGrant,
  customLogicIds: population.definition.customLogicIds,
  eligibilityCriterias: population.definition.eligibilityCriterias,
});

export const toDraftDtos = (
  draft: PopulationDraftState
): CustomPopulationDraftDto[] =>
  draft.populations.map((population) => ({
    id: population.id,
    clientKey: population.clientKey,
    definition: population.definition,
  }));

export const buildPopulationActions = (
  draft: PopulationDraftState,
  persistedIds: number[]
): CustomPopulationActionDto[] => {
  const draftIds = new Set(
    draft.populations.map((population) => population.id)
  );

  const deletions = persistedIds
    .filter((id) => !draftIds.has(id))
    .map((id) => ({ action: CustomPopulationActionDtoAction.DELETE, id }));

  const upserts = draft.populations.map((population) =>
    population.id != null
      ? {
          action: CustomPopulationActionDtoAction.UPDATE,
          id: population.id,
          population: population.definition,
        }
      : {
          action: CustomPopulationActionDtoAction.CREATE,
          clientKey: population.clientKey,
          population: population.definition,
        }
  );

  return [...deletions, ...upserts];
};

const formatEligibilityValue = (block: SpecialAwardCriteriaBlock): string => {
  switch (block.criteria) {
    case EligibilityCriteriaDtoCriteria.CEEB:
      return '';
    case EligibilityCriteriaDtoCriteria.DISTANCE:
      return `${block.amountFrom}–${block.amountTo}`;
    case EligibilityCriteriaDtoCriteria.APP_DATE:
      return `${block.dateFrom}–${block.dateTo}`;
    default:
      return block.operand === EligibilityCriteriaDtoOperand.BETWEEN
        ? `${block.amountFrom}–${block.amountTo}`
        : block.amount;
  }
};

const getEligibilityLabel = (block: SpecialAwardCriteriaBlock): string => {
  const criteriaLabel =
    ELIGIBILITY_CRITERIA_OPTIONS.find(
      (option) => option.value === block.criteria
    )?.label ?? '';
  const operandLabel =
    ELIGIBILITY_OPERAND_OPTIONS.find((option) => option.value === block.operand)
      ?.label ?? '';
  const value = formatEligibilityValue(block);
  const isSymbolicOperand =
    !!operandLabel && block.operand !== EligibilityCriteriaDtoOperand.BETWEEN;

  if (isSymbolicOperand) {
    return `${criteriaLabel}${operandLabel}${value}`;
  }

  return [criteriaLabel, value].filter(Boolean).join(' ');
};

const getStudentDataLabel = (
  block: SpecialAwardCriteriaBlock,
  categories: SpecialAwardLogicCategory[]
): string => {
  const firstOptionId = block.optionIds[0];
  const option = categories
    .find((category) => category.category === block.category)
    ?.options?.find((item) => String(item.id) === String(firstOptionId));

  return option?.name ?? block.category;
};

const getBlockLabel = (
  block: SpecialAwardCriteriaBlock,
  categories: SpecialAwardLogicCategory[]
): string => {
  if (block.type === 'STUDENT_DATA') {
    return getStudentDataLabel(block, categories);
  }

  if (block.type === 'ELIGIBILITY') {
    return getEligibilityLabel(block);
  }

  return '';
};

export const buildPopulationName = (
  appliesTo: SpecialAwardAppliesTo,
  criteriaBlocks: SpecialAwardCriteriaBlock[],
  categories: SpecialAwardLogicCategory[]
): string => {
  const preset = SPECIAL_AWARD_PRESETS.find((item) => appliesTo[item.key]);
  const label =
    preset?.label ??
    criteriaBlocks
      .map((block) => getBlockLabel(block, categories))
      .find(Boolean) ??
    '';

  return label.slice(0, POPULATION_NAME_MAX_LENGTH);
};

export const toPopulationDefinition = (
  name: string,
  request: Pick<
    CustomPopulationRequest,
    'campusVisit' | 'pellGrant' | 'fafsaGrant' | 'customLogicIds'
  > & { eligibilityCriterias?: EligibilityCriteriaDto[] }
): CustomPopulationRequest => ({
  name,
  campusVisit: !!request.campusVisit,
  pellGrant: !!request.pellGrant,
  fafsaGrant: !!request.fafsaGrant,
  customLogicIds: request.customLogicIds ?? [],
  eligibilityCriterias: request.eligibilityCriterias ?? [],
});

export interface NotIncludedStudents {
  notIncluded: number;
  total: number;
}

const PERCENT_MULTIPLIER = 100;

export const formatNotIncludedMessage = ({
  notIncluded,
  total,
}: NotIncludedStudents): string | null => {
  if (notIncluded <= 0 || total <= 0) {
    return null;
  }

  const percent = Math.round((notIncluded / total) * PERCENT_MULTIPLIER);

  return `${notIncluded} of ${total} students are not included / ${percent}% of modeled population`;
};
